import { useCallback, useEffect, useMemo, useState } from 'react';
import * as React from 'react';
import styled from 'styled-components';

import { Cell } from '../Cell';
import { Chart } from '../Chart';
import { DataConnectionName } from '../DataConnectionName';
import { ErrorOutput } from '../ErrorOutput';
import { Table } from '../Table';

import { MagicEditBar } from './MagicEditBar';
import { MagicElement } from './MagicElement';
import {
  Blue,
  Code,
  DEFAULT_CELL_DEMO_CONFIG,
  type DefaultCellProps,
  Inner,
  Line,
  LineNumbers,
  type MagicEditBarMode,
  type MagicElementState,
  Pink,
  Result,
  ResultContent,
  ResultHeader,
  type ResultType,
  StyledMinimalButton,
  TAOption,
  Teal,
  Typeahead,
  Wrapper
} from './sharedStyles';

const Source = styled.div`
  display: flex;
  align-items: center;
  padding: 0 4px;
  height: 20px;
  background: ${({ theme }) => theme.appUI.backgroundColor.MUTED};
  position: absolute;
  top: 5px;
  right: 5px;
`;

interface SQLCellProps extends DefaultCellProps {
  hideConnection?: boolean;
  name: string;
  outputName?: string;
  withdbt?: boolean;
}

export const SQLCell: React.ComponentType<SQLCellProps> = React.memo(
  ({
    commentPopupOverride,
    name,
    fixedHeight,
    headerStyle = 'floating',
    hideOutputToggle = false,
    defaultResultType = 'chart',
    outputName = 'dataframe',
    hideHeader = false,
    showCommentCount = false,
    showLineNumbers = false,
    showTypeAhead = false,
    showResult = false,
    tableRowCount,
    demoConfig = DEFAULT_CELL_DEMO_CONFIG
  }) => {
    const [resultOption, setResultOption] =
      useState<ResultType>(defaultResultType);
    const [isMagicBarOpen, setIsMagicBarOpen] = useState(false);
    const [isMagicLoading, setIsMagicLoading] = useState(false);
    const [isMagicPopoverOpen, setIsMagicPopoverOpen] = useState(false);
    const [magicEditBarMode, setMagicEditBarMode] =
      useState<MagicEditBarMode>('generate');

    useEffect(() => {
      setIsMagicBarOpen(demoConfig.isMagicBarOpen);
      setIsMagicLoading(demoConfig.isMagicLoading);
      setIsMagicPopoverOpen(demoConfig.isMagicPopoverOpen);
      setMagicEditBarMode(demoConfig.magicEditBarMode);
      if (demoConfig.resultType) {
        setResultOption(demoConfig.resultType);
      }
    }, [demoConfig]);

    const toggleMagicBar = useCallback(() => {
      setIsMagicBarOpen(!isMagicBarOpen);
    }, [isMagicBarOpen]);

    const magicState: MagicElementState = useMemo(() => {
      if (isMagicLoading) {
        return 'loading';
      }
      if (isMagicBarOpen) {
        return 'active';
      }
      return 'default';
    }, [isMagicBarOpen, isMagicLoading]);

    const sqlCode = demoConfig.code || (
      <>
        <Line>
          <Blue>select</Blue>
        </Line>
        <Line $indent={true}>
          <Pink>cast</Pink>(<Blue>&#123;&#123;timeframe&#125;&#125;</Blue> as
          timestamp) <Blue>as</Blue> <Pink>month</Pink>,
        </Line>
        <Line $indent={true}>ship, destination,</Line>
        <Line $indent={true}>
          <Pink>sum</Pink>(spice_tons) <Blue>as</Blue> spice_tons
        </Line>
        <Line>
          {showTypeAhead ? (
            <>
              <Blue>from</Blue> atreides.v
              <Typeahead>
                <TAOption>vehicle_arrivals</TAOption>
                <TAOption>vehicle_destinations</TAOption>
                <TAOption>vehicle_id</TAOption>
              </Typeahead>
            </>
          ) : (
            <>
              <Blue>from</Blue> atreides.vehicle_destinations
            </>
          )}
        </Line>
        <Line>
          <Blue>group by</Blue> <Teal>1</Teal>,<Teal>2</Teal>,<Teal>3</Teal>
        </Line>
      </>
    );

    return (
      <Cell
        commentPopupOverride={commentPopupOverride}
        fixedHeight={fixedHeight}
        headerStyle={headerStyle}
        hideHeader={hideHeader}
        label="SQL cell"
        outputName={outputName}
        showCommentCount={showCommentCount}>
        <Wrapper>
          <Source>
            <DataConnectionName name={name} />
          </Source>
          <Inner>
            {demoConfig.isMagicEnabled && (
              <>
                <MagicElement
                  popupOverride={isMagicPopoverOpen}
                  state={magicState}
                  stillThinking={false}
                />
                <MagicEditBar
                  isOpen={isMagicBarOpen}
                  mode={magicEditBarMode}
                  promptText={demoConfig.magicPrompt}
                  onClose={toggleMagicBar}
                />
              </>
            )}
            <Code $showLineNumbers={showLineNumbers}>
              {showLineNumbers && (
                <LineNumbers>
                  <span>1</span>
                  <span>2</span>
                  <span>3</span>
                  <span>4</span>
                  <span>5</span>
                  <span>6</span>
                  <span>7</span>
                  <span>8</span>
                  <span>9</span>
                  <span>10</span>
                  <span>11</span>
                  <span>12</span>
                  <span>13</span>
                  <span>14</span>
                  <span>15</span>
                  <span>16</span>
                  <span>17</span>
                  <span>18</span>
                  <span>19</span>
                  <span>20</span>
                </LineNumbers>
              )}
              {sqlCode}
            </Code>
          </Inner>
          {showResult && (
            <Result>
              {!hideOutputToggle && (
                <ResultHeader>
                  <StyledMinimalButton
                    $selected={resultOption === 'chart'}
                    onClick={() => setResultOption('chart')}>
                    Chart
                  </StyledMinimalButton>
                  <StyledMinimalButton
                    $selected={resultOption === 'table'}
                    onClick={() => setResultOption('table')}>
                    Table
                  </StyledMinimalButton>
                </ResultHeader>
              )}
              <ResultContent>
                {resultOption === 'chart' && <Chart />}
                {resultOption === 'table' && <Table rowCount={tableRowCount} />}
                {resultOption === 'error' && (
                  <ErrorOutput
                    description="Unknown function 'DATE_TRUN'"
                    title="Your query could not be executed"
                  />
                )}
              </ResultContent>
            </Result>
          )}
        </Wrapper>
      </Cell>
    );
  }
);
